import React, { useEffect, useState } from 'react';
import { X, Edit3, AlertCircle, Lock, Loader, Trash2 } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { pickingService } from '../../../../services/pickingService';
import { NumericInput } from '../../../common/inputs';
import { PListLineDto } from '../../../../types/picking';

interface ChangeLineQuantityModalProps {
  isOpen: boolean;
  pListId: number;
  line: PListLineDto | null;
  /** Operatøren som har listen låst akkurat nå — endring er tillatt, men vises som info. */
  lockedBy?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const formatQty = (value: number) =>
  Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, '');

/**
 * Lagersjef «Endre antall» på en plukklistelinje — kun Compass GO (TW 36409951).
 * Øker eller reduserer bestilt antall; ordrelinjen endres også, så ERP får nytt bestilt antall
 * ved avslutning. Nytt antall kan ikke være lavere enn det som allerede er plukket.
 *
 * «Slett linje» (TW 36411059) ligger nederst: sletter plukklinjen og ordrelinjen, som i bizClick.
 * Sperret (med årsak synlig) når linjen er plukket eller er ordrens siste på listen.
 */
export const ChangeLineQuantityModal: React.FC<ChangeLineQuantityModalProps> = ({
  isOpen,
  pListId,
  line,
  lockedBy,
  onClose,
  onSaved
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isOpen && line) {
      setValue(formatQty(line.qty));
      setError(null);
      setSaving(false);
      setConfirmDelete(false);
      setDeleting(false);
    }
  }, [isOpen, line]);

  if (!isOpen || !line) return null;

  const k = 'manager.modules.newPickingLists.changeQuantity';
  const unit = line.orderLineItemUnitTxt || '';
  const newQty = value.trim() === '' ? NaN : parseFloat(value);
  const picked = line.qtyPicked ?? 0;
  const isValidNumber = !Number.isNaN(newQty) && newQty > 0;
  const isBelowPicked = isValidNumber && newQty < picked;
  const isUnchanged = isValidNumber && Math.abs(newQty - line.qty) < 0.0001;
  const canSave = isValidNumber && !isBelowPicked && !isUnchanged && !saving && !deleting;
  const deleteBlockedReason = line.deleteLineBlockedReason ?? null;
  const canDelete = !deleteBlockedReason && !saving && !deleting;
  const delta = isValidNumber ? newQty - line.qty : 0;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      setError(null);
      await pickingService.changeLineQuantity(pListId, line.pListLineNo, newQty);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t(`${k}.saveError`, {}, 'Kunne ikke endre antall'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    try {
      setDeleting(true);
      setError(null);
      await pickingService.deleteLine(pListId, line.pListLineNo);
      onSaved();
      onClose();
    } catch (err) {
      setConfirmDelete(false);
      setError(err instanceof Error ? err.message : t(`${k}.deleteError`, {}, 'Kunne ikke slette linjen'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Edit3 size={18} className="text-gray-600" />
            {t(`${k}.title`, {}, 'Endre antall')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={t('common.buttons.close', {}, 'Lukk')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4">
          {/* Linjeinfo */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
            <div className="font-mono font-semibold text-gray-900">{line.itemNo}</div>
            {line.itemDescription && <div className="text-gray-700">{line.itemDescription}</div>}
            <div className="text-gray-600">
              {t(`${k}.order`, { '0': line.orderNoERP || String(line.orderNo), '1': String(line.lineNoERP ?? line.lineNo) }, `Ordre ${line.orderNoERP || line.orderNo}, linje ${line.lineNoERP ?? line.lineNo}`)}
            </div>
            <div className="flex gap-6 pt-1">
              <div>
                <div className="text-xs text-gray-500">{t(`${k}.currentQty`, {}, 'Antall i dag')}</div>
                <div className="font-semibold text-gray-900 tabular-nums">{formatQty(line.qty)} {unit}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">{t(`${k}.alreadyPicked`, {}, 'Allerede plukket')}</div>
                <div className="font-semibold text-gray-900 tabular-nums">{formatQty(picked)} {unit}</div>
              </div>
            </div>
          </div>

          {lockedBy && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <Lock size={16} className="flex-shrink-0 mt-0.5" />
              <span>{t(`${k}.lockedInfo`, { '0': lockedBy }, `Listen plukkes nå av ${lockedBy}. Operatøren ser det nye antallet når plukkbildet oppdateres.`)}</span>
            </div>
          )}

          {/* Nytt antall */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="change-line-qty">
              {t(`${k}.newQty`, {}, 'Nytt antall')}
            </label>
            <div className="flex items-center gap-2">
              <NumericInput
                id="change-line-qty"
                mode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                autoFocus
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base min-h-[44px] bg-white tabular-nums"
              />
              {unit && <span className="text-sm text-gray-600">{unit}</span>}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {picked > 0
                ? t(`${k}.minHint`, { '0': formatQty(picked) }, `Kan økes eller reduseres, men ikke under ${formatQty(picked)} (allerede plukket).`)
                : t(`${k}.hint`, {}, 'Kan økes eller reduseres. Må være større enn 0.')}
            </p>
            {isBelowPicked && (
              <p className="mt-1 text-sm text-red-600">
                {t(`${k}.belowPicked`, { '0': formatQty(picked) }, `Antall kan ikke være lavere enn det som er plukket (${formatQty(picked)}).`)}
              </p>
            )}
            {isValidNumber && !isBelowPicked && !isUnchanged && (
              <p className={`mt-1 text-sm font-medium ${delta > 0 ? 'text-green-700' : 'text-orange-700'}`}>
                {delta > 0
                  ? t(`${k}.increase`, { '0': formatQty(delta) }, `Økning: +${formatQty(delta)}`)
                  : t(`${k}.decrease`, { '0': formatQty(-delta) }, `Reduksjon: −${formatQty(-delta)}`)}
              </p>
            )}
          </div>

          <p className="text-xs text-gray-500">
            {t(`${k}.erpInfo`, {}, 'Ordrelinjen endres også. ERP får nytt bestilt antall og plukket antall når plukklisten avsluttes.')}
          </p>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Slett linje (TW 36411059) — sperret med synlig årsak, bekreftes i dialogen */}
          <div className="border-t border-gray-200 pt-4">
            {confirmDelete ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-3">
                <div className="text-sm font-semibold text-red-800">
                  {t(`${k}.deleteConfirmTitle`, {}, 'Slette linjen?')}
                </div>
                <p className="text-sm text-red-700">
                  {t(`${k}.deleteConfirmText`, { '0': line.itemNo || '' }, `${line.itemNo} fjernes fra plukklisten og ordren. Linjen kommer ikke med i plukkvitteringen til ERP.`)}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 min-h-[44px]"
                  >
                    {t('common.buttons.cancel', {}, 'Avbryt')}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-2 rounded-lg font-medium min-h-[44px] flex items-center gap-2 bg-red-600 text-white hover:bg-red-700"
                  >
                    {deleting ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    {t(`${k}.deleteConfirm`, {}, 'Ja, slett linjen')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={!canDelete}
                  className={`px-4 py-2 rounded-lg font-medium min-h-[44px] flex items-center gap-2 border ${
                    canDelete
                      ? 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                      : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                  }`}
                >
                  <Trash2 size={16} />
                  {t(`${k}.deleteButton`, {}, 'Slett linje')}
                </button>
                {deleteBlockedReason && (
                  <p className="text-xs text-gray-500">{deleteBlockedReason}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 min-h-[44px]"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`px-4 py-2 rounded-lg font-medium min-h-[44px] flex items-center gap-2 ${
              canSave ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving && <Loader size={16} className="animate-spin" />}
            {t('common.buttons.save', {}, 'Lagre')}
          </button>
        </div>
      </div>
    </div>
  );
};
